import React, { useState, useEffect } from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import Table from "../../core/pagination/datatable";
import { getCurrentStockReport } from "../../core/api/reportService";

const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const CATEGORY_OPTIONS = [
  { value: "", label: "All Categories" },
  { value: "Medicine", label: "Medicine" },
  { value: "Consumable", label: "Consumable" },
];
const DEFAULT_FILTERS = { category: "", lowStockOnly: false };

const StockReport = () => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { runReport(); }, []);

  const runReport = async (f = filters) => {
    setLoading(true);
    try {
      const res = await getCurrentStockReport(f);
      const payload = res.data || {};
      setData(payload.records || payload || []);
    } catch {
      Swal.fire("Error", "Failed to load stock report.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    runReport(DEFAULT_FILTERS);
  };

  const columns = [
    { title: "Item Name", dataIndex: "itemName", sorter: (a, b) => (a.itemName || "").localeCompare(b.itemName || "") },
    {
      title: "Category", dataIndex: "itemCategory", width: 110,
      render: (v) => <span className={`badge bg-${v === "Medicine" ? "primary" : "info"}`}>{v}</span>,
    },
    { title: "Unit", dataIndex: "unit", width: 80 },
    {
      title: "Current Stock", dataIndex: "currentStock", align: "center", width: 120,
      render: (v, rec) => (
        <span className={rec.minStockLevel && v <= rec.minStockLevel ? "text-danger fw-bold" : ""}>
          {v ?? 0} {rec.minStockLevel && v <= rec.minStockLevel && "⚠"}
        </span>
      ),
      sorter: (a, b) => (a.currentStock || 0) - (b.currentStock || 0),
    },
    { title: "Min Stock", dataIndex: "minStockLevel", align: "center", width: 90 },
    { title: "Purchase Rate", dataIndex: "purchaseRate", align: "right", width: 120, render: fmt },
    { title: "Selling Rate", dataIndex: "sellingRate", align: "right", width: 110, render: fmt },
    {
      title: "Stock Value", dataIndex: "stockValue", align: "right", width: 120,
      render: (v, rec) => fmt(v ?? (rec.currentStock || 0) * (rec.purchaseRate || 0)),
      sorter: (a, b) => ((a.stockValue || 0) - (b.stockValue || 0)),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>Current Stock Report</h4><h6>Live inventory levels across all products</h6></div>
        </div>
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col" style={{ minWidth: 180, maxWidth: 220 }}>
                <label className="form-label small mb-1">Category</label>
                <Select classNamePrefix="react-select" options={CATEGORY_OPTIONS}
                  value={CATEGORY_OPTIONS.find((o) => o.value && o.value === filters.category) || null}
                  placeholder="All Categories"
                  isClearable
                  onChange={(opt) => setFilters((f) => ({ ...f, category: opt?.value || "" }))} />
              </div>
              <div className="col-auto d-flex align-items-center gap-2 pt-3">
                <input type="checkbox" id="lowStock" className="form-check-input"
                  checked={filters.lowStockOnly}
                  onChange={(e) => setFilters((f) => ({ ...f, lowStockOnly: e.target.checked }))} />
                <label htmlFor="lowStock" className="form-check-label small">Low Stock Only</label>
              </div>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={() => runReport()} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
              <div className="col-auto">
                <button className="btn btn-dark" onClick={handleReset} disabled={loading}>
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
            {data.length > 0 && (
              <div className="mt-2 text-end text-muted small">
                Total stock value: <strong className="text-dark">
                  {fmt(data.reduce((s, r) => s + (r.stockValue ?? (r.currentStock || 0) * (r.purchaseRate || 0)), 0))}
                </strong>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockReport;
